/**
 * Helpers for the For Review tab — live pending queue, not stale event snapshots.
 */
import type {
  TriggerEventRow,
  TriggerEventRuleOutcome,
  TriggerPendingAction,
} from "@/services/api/agentic-v2/types";

export function pendingIdFromOutcome(
  outcome: TriggerEventRuleOutcome
): string | null {
  if (outcome.outcome !== "pending_approval") return null;
  const id = outcome.detail?.pending_id;
  return typeof id === "string" ? id : null;
}

export function activePendingCount(
  event: TriggerEventRow,
  pendingIds: ReadonlySet<string>
): number {
  return pendingIdsForEvent(event, pendingIds).length;
}

/** Pending action ids on this event that are still in the live queue. */
export function pendingIdsForEvent(
  event: TriggerEventRow,
  pendingIds: ReadonlySet<string>
): string[] {
  const ids: string[] = [];
  for (const rule of event.rules) {
    const id = pendingIdFromOutcome(rule);
    if (id !== null && pendingIds.has(id)) ids.push(id);
  }
  return ids;
}

/** Rules that ran or were approved since the event was logged. */
export function resolvedRanCount(
  event: TriggerEventRow,
  pendingIds: ReadonlySet<string>
): number {
  return event.rules.filter((r) => {
    if (r.outcome === "matched") return true;
    const id = pendingIdFromOutcome(r);
    return r.outcome === "pending_approval" && id !== null && !pendingIds.has(id);
  }).length;
}

export function isStillPending(
  outcome: TriggerEventRuleOutcome,
  pendingIds: ReadonlySet<string>
): boolean {
  const id = pendingIdFromOutcome(outcome);
  return id !== null && pendingIds.has(id);
}

export function wasApprovedSinceLogged(
  outcome: TriggerEventRuleOutcome,
  pendingIds: ReadonlySet<string>
): boolean {
  const id = pendingIdFromOutcome(outcome);
  return outcome.outcome === "pending_approval" && id !== null && !pendingIds.has(id);
}

export function webhookLivePending(
  runId: string,
  events: TriggerEventRow[],
  pendingIds: ReadonlySet<string>
): number {
  return events
    .filter((e) => e.webhook_run_id === runId)
    .reduce((n, e) => n + activePendingCount(e, pendingIds), 0);
}

function pendingActionToEventRow(p: TriggerPendingAction): TriggerEventRow {
  const catalogueKey =
    typeof p.action_config?.catalogue_key === "string"
      ? p.action_config.catalogue_key
      : undefined;
  return {
    _id: `pending-${p._id}`,
    client_id: p.client_id,
    event_type: p.event_type,
    source: p.event_source ?? "plannr_sync",
    occurred_at: p.created_at,
    entity_refs: p.entity_refs,
    fa_client_id: p.fa_client_id,
    client_display_name: p.client_display_name,
    plannr_account_uuid: p.plannr_account_uuid,
    payload: p.payload,
    rules_matched: 0,
    rules_pending: 1,
    rules_total: 1,
    rules: [
      {
        rule_id: p.rule_id,
        rule_name: p.rule_name,
        action_type: p.action_type,
        outcome: "pending_approval",
        detail: {
          pending_id: p._id,
          status: "pending",
          action_type: p.action_type,
          rule_name: p.rule_name,
          ...(catalogueKey ? { catalogue_key: catalogueKey } : {}),
        },
      },
    ],
    timestamp: p.created_at,
  };
}

/** Events that still have at least one open pending action, plus orphan pendings. */
export function buildReviewEvents(
  events: TriggerEventRow[],
  pending: TriggerPendingAction[]
): TriggerEventRow[] {
  const pendingIds = new Set(pending.map((p) => p._id));
  if (pendingIds.size === 0) return [];

  const rows: TriggerEventRow[] = [];
  const coveredPendingIds = new Set<string>();

  for (const event of events) {
    if (activePendingCount(event, pendingIds) === 0) continue;
    rows.push(event);
    for (const rule of event.rules) {
      const id = pendingIdFromOutcome(rule);
      if (id) coveredPendingIds.add(id);
    }
  }

  for (const p of pending) {
    if (!coveredPendingIds.has(p._id)) {
      rows.push(pendingActionToEventRow(p));
    }
  }

  return rows.sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
}
